import React, { useState, useEffect, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import Swiper from 'swiper'
import 'swiper/css'
import Preview from '@renderer/components/Preview'
import { Countdown } from '@renderer/utils/countdownUtils'
import { useConfigStore } from '@renderer/stores/configState'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { unifiedCameraUtils } from '@renderer/utils/cameras/uniCamUtils'
import { useCachedBackgroundImage } from '@renderer/hooks/useCachedBackgroundImage'
import { useCachedImage } from '@renderer/hooks/useCachedImage'

const FilterPage: React.FC = () => {
  const navigate = useNavigate()
  const [activeIndex, setActiveIndex] = useState(0)
  const [remainingTime, setRemainingTime] = useState<string>('--:--')
  const swiperRef = useRef<Swiper | null>(null)
  const { filters, getPageSettings } = useConfigStore()
  const { setSessionState } = useSessionStateStore()
  const capturePreview = unifiedCameraUtils.capturePreview
  const filterTimeout = getPageSettings('filter')?.filterTimeout || 0
  const countdownInstance = useRef<Countdown | null>(null)
  const { projectId } = useParams()

  useEffect((): (() => void) => {
    swiperRef.current = new Swiper('.mainSwiper', {
      loop: false,
      slidesPerView: 3,
      centeredSlides: true,
      spaceBetween: 10,
      direction: 'horizontal'
    })

    swiperRef.current.on('realIndexChange', (swiper) => {
      setActiveIndex(swiper.realIndex)
    })

    if (filters.length > 0) {
      swiperRef.current.slideToLoop(1)
    }

    if (filterTimeout > 0) {
      countdownInstance.current = new Countdown(
        filterTimeout,
        () => setRemainingTime(countdownInstance.current!.getFormattedRemainingSeconds()),
        () => {
          setSessionState({
            timeline: {
              activityLogs: [
                {
                  page: 'filter',
                  type: 'selection',
                  action: 'timeout',
                  details: {
                    filterId: filters[activeIndex]._id,
                    timeoutDuration: filterTimeout
                  },
                  timestamp: new Date()
                }
              ]
            }
          })
          continueToPhotoSession()
        }
      )
      countdownInstance.current.start()
    }

    return () => {
      if (swiperRef.current) swiperRef.current.destroy()
      countdownInstance.current?.stop()
    }
  }, [])

  const continueToPhotoSession = async (): Promise<void> => {
    const selectedFilter = filters[activeIndex]
    setSessionState({
      userPreferences: {
        filterId: selectedFilter._id
      },
      timeline: {
        activityLogs: [
          {
            page: 'filter',
            type: 'selection',
            action: 'complete',
            details: { filterId: selectedFilter._id },
            timestamp: new Date()
          }
        ]
      }
    })

    navigate(`/${projectId}/frame`)
  }

  const slidePrev = (): void => {
    swiperRef.current?.slidePrev()
  }

  const slideNext = (): void => {
    swiperRef.current?.slideNext()
  }

  const handleClick = (index: number): void => {
    swiperRef.current?.slideToLoop(index)
  }

  // Call all hooks at the top level
  const cachedFilterBg = useCachedBackgroundImage('/assets/photophoria/photophoria-filter.webp')
  const cachedFilterPrev = useCachedImage('/assets/photophoria/photophoria-filter-prev.webp')
  const cachedFilterNext = useCachedImage('/assets/photophoria/photophoria-filter-next.webp')
  const cachedFilterOption1 = useCachedImage('/assets/photophoria/photophoria-filter-option-1.webp')
  const cachedFilterOption2 = useCachedImage('/assets/photophoria/photophoria-filter-option-2.webp')
  const cachedFilterOption3 = useCachedImage('/assets/photophoria/photophoria-filter-option-3.webp')

  return (
    <div
      className="relative w-full h-full bg-cover bg-center"
      style={{
        backgroundImage: `url(${cachedFilterBg})`
      }}
    >
      <div className="absolute top-[150px] left-1/2 -translate-x-1/2 w-3/4 h-[700px] flex justify-between items-center">
        <button
          onClick={slidePrev}
          className="mx-8 hover:opacity-90 transition-opacity"
          aria-label="Previous filter"
        >
          <img src={cachedFilterPrev} alt="Previous" className="w-24 h-auto" />
        </button>
        <div className="swiper mainSwiper w-full flex items-center justify-center">
          <div className="swiper-wrapper cursor-grab p-0 active:cursor-grabbing focus:cursor-grabbing w-full transition-all duration-300">
            {filters.map((filter, index) => (
              <div
                className="swiper-slide flex flex-col p-0 items-center justify-center transition-all h-[700px] duration-300"
                key={filter._id}
              >
                {/* Filter Card Container */}
                <div
                  onClick={() => handleClick(index)}
                  className={`relative w-[400px] h-[400px] flex flex-col items-center justify-center transition-all duration-300 cursor-pointer
                    ${activeIndex === index ? '' : 'mt-8 scale-90'}`}
                >
                  {/* Preview Component (behind frame) */}
                  <div className="absolute top-[50px] left-[25px] w-[350px] h-[320px] overflow-hidden rounded-lg z-0">
                    <Preview
                      capturePreview={capturePreview}
                      adjustments={filter.adjustments}
                      filter={filter._id}
                      ratio={1}
                      cameraRatio={1}
                      previewQuality={{
                        maxWidth: 350,
                        maxHeight: 350,
                        quality: 0.8
                      }}
                    />
                  </div>
                  {/* Frame Image (in front of preview) */}
                  <img
                    src={
                      (index % 3) + 1 === 1
                        ? cachedFilterOption1
                        : (index % 3) + 1 === 2
                          ? cachedFilterOption2
                          : cachedFilterOption3
                    }
                    alt={`Filter Frame ${(index % 3) + 1}`}
                    className="absolute inset-0 w-full h-full object-contain z-10"
                  />
                  {/* Filter Name (optional, position as needed) */}
                  {/* <div className="text-center absolute bottom-[-40px] left-1/2 -translate-x-1/2 z-20">
                    <h2
                      className={`text-carnabyTan font-deco-samu transition-all duration-300 
                      ${activeIndex === index ? 'text-4xl' : 'text-3xl'}`}
                    >
                      {filter.name}
                    </h2>
                  </div> */}
                </div>
              </div>
            ))}
          </div>
        </div>
        <button
          onClick={slideNext}
          className="mx-8 hover:opacity-90 transition-opacity"
          aria-label="Next filter"
        >
          <img src={cachedFilterNext} alt="Next" className="w-24 h-auto" />
        </button>
      </div>
      <div className="absolute bottom-[30px] right-[50px] flex flex-col items-center">
        <button
          onClick={continueToPhotoSession}
          className="transition-transform transform hover:scale-105"
        >
          <img src={cachedFilterNext} alt="Continue" className="w-32 h-auto" />
        </button>
        <span className="text-gray-500 text-lg font-serif-bahela">{remainingTime}</span>
      </div>
    </div>
  )
}

export default FilterPage
